import React, { useState, useRef, useCallback, useEffect } from "react";
import { Drawer, Button, Space, message, Alert } from "antd";
import { scheduleService } from "../../../../api/enhancedApi";
import FlightCompare from "../../ScheduleEditor/components/FlightCompare";

/**
 * 计划确认抽屉组件
 * @param {Object} props
 * @param {boolean} props.visible - 是否显示抽屉
 * @param {function} props.onClose - 关闭抽屉回调
 * @param {Object} props.schedule - 计划对象
 * @param {function} props.onConfirm - 确认成功后的回调
 */
const ScheduleConfirmDrawer = ({ visible, onClose, schedule, onConfirm }) => {
    const [loading, setLoading] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [compareResult, setCompareResult] = useState(null);
    const [manualMatchings, setManualMatchings] = useState([]);
    const compareRef = useRef(null);

    // 加载计划参数和已保存的对比数据
    const loadScheduleParameters = useCallback(async () => {
        if (!schedule?.id) return;

        try {
            setLoading(true);
            // 加载计划详情，里面已经包含了 parameter 信息
            const detailResponse = await scheduleService.getScheduleDetail(schedule.id, { limit: 1 });
            const scheduleDetail = detailResponse?.data;

            // 直接从 scheduleDetail 中获取 parameter_id 和 parameter 对象
            const parameterData = scheduleDetail?.parameter;

            let savedMatchings = [];
            let savedCompareResult = null;

            // 检查并提取已保存的匹配数据
            if (parameterData?.parameter_json?.flight_compare_result) {
                const savedData = parameterData.parameter_json.flight_compare_result;

                // 提取手动匹配数据
                if (savedData.manualMatchings) {
                    savedMatchings = savedData.manualMatchings;
                    setManualMatchings(savedMatchings);
                }

                // 如果有保存的对比详情，也恢复对比结果状态
                if (savedData.comparisonDetails) {
                    savedCompareResult = savedData.comparisonDetails;
                    setCompareResult(savedCompareResult);
                    console.log("已恢复之前保存的对比结果:", savedData);
                }
            }

            // 无论是否找到匹配，都尝试加载对比组件
            if (compareRef.current && visible) {
                compareRef.current.loadCurrentOperations(savedMatchings);
            }
        } catch (error) {
            console.error("加载计划详情或处理参数时失败:", error);
            // 加载失败时仍然尝试正常加载对比
            if (compareRef.current && visible) {
                compareRef.current.loadCurrentOperations();
            }
        } finally {
            setLoading(false);
        }
    }, [schedule?.id, visible]);

    // 当抽屉打开时加载参数和对比数据
    useEffect(() => {
        if (visible && schedule?.id) {
            loadScheduleParameters();
        }
    }, [visible, schedule?.id, loadScheduleParameters]);

    // 处理手动匹配变更
    const handleMatchingsChange = (matchings) => {
        setManualMatchings(matchings);
    };

    // 处理对比结果变更
    const handleCompareResult = (result) => {
        setCompareResult(result);
    };

    // 确认计划
    const confirmSchedule = async () => {
        if (!schedule?.id) return;

        try {
            setConfirmLoading(true);
            message.loading("正在确认计划...", 0);

            // 如果没有对比结果，提示用户
            if (!compareResult) {
                message.destroy();
                message.warning("请先完成对比分析");
                setConfirmLoading(false);
                return;
            }

            // 1. 获取前端对比结果数据
            const comparisonDetails = {
                addedFlights: compareResult.addedFlights || [],
                removedOperations: compareResult.removedOperations || [], // 这里保持ID数组格式，供后端使用
                changedPairs: compareResult.changedPairs || [],
                manualMatchings: [],
            };

            // 2. 处理手动匹配关系
            if (manualMatchings && manualMatchings.length > 0) {
                manualMatchings.forEach((matching) => {
                    const { leftIds, rightKeys, type } = matching;

                    // 根据匹配类型构建匹配信息
                    comparisonDetails.manualMatchings.push({
                        replacement_type: type, // MANUAL, MERGE, SPLIT, MULTI_REPLACE
                        replaced_operation_ids: leftIds || [],
                        replaces_draft_keys: rightKeys || [],
                        manual: true,
                    });
                });
            }

            // 3. 保存对比结果到计划参数（用于发布阶段恢复）
            const saveData = {
                comparisonDetails: comparisonDetails,
                manualMatchings: manualMatchings,
                timestamp: new Date().toISOString(),
            };

            // 先尝试保存对比结果
            try {
                await scheduleService.saveFlightCompareResult(schedule.id, saveData);
                console.log("对比结果已保存到计划参数");
            } catch (saveError) {
                console.warn("保存对比结果失败，但继续确认流程:", saveError);
            }

            // 4. 调用确认 API
            await scheduleService.updateScheduleStatus(schedule.id, "AIRLINE_CONFIRMED");

            message.destroy();
            message.success("计划确认成功");
            onConfirm && onConfirm();
            onClose();
        } catch (error) {
            message.destroy();
            message.error(`确认失败: ${error.message || "未知错误"}`);
            console.error("确认计划失败:", error);
        } finally {
            setConfirmLoading(false);
        }
    };

    return (
        <Drawer
            title={`确认计划: ${schedule?.name || "未命名计划"}`}
            placement="right"
            width="95%"
            onClose={onClose}
            open={visible}
            extra={
                <Space>
                    <Button onClick={onClose}>取消</Button>
                    <Button type="primary" loading={confirmLoading} onClick={confirmSchedule} disabled={!compareResult}>
                        确认计划
                    </Button>
                </Space>
            }
            destroyOnHidden={true}
        >
            <div className="confirm-drawer-content">
                <div className="confirm-instructions">
                    <Alert
                        message="确认前请检查"
                        description={
                            <div>
                                <p>确认操作将会：</p>
                                <ul>
                                    <li>将当前计划状态更新为"航司确认"</li>
                                    <li>保存对比结果和匹配关系，供后续发布使用</li>
                                    <li>锁定计划内容，防止意外修改</li>
                                </ul>
                                <p>请仔细检查对比结果并根据需要调整匹配关系后再确认。</p>
                            </div>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                </div>

                <div className="compare-container">
                    <FlightCompare
                        ref={compareRef}
                        scheduleData={{ schedule, flights: schedule?.flights || [] }}
                        onMatchingsChange={handleMatchingsChange}
                        onCompareResult={handleCompareResult}
                        manualMatchings={manualMatchings}
                        mode="confirm" // 确认模式
                    />
                </div>
            </div>
        </Drawer>
    );
};

export default ScheduleConfirmDrawer;
